import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as authApi from '../api/auth.api';
import storage from '../utils/storage';

/**
 * Hook pour gérer l'authentification admin
 * Gère l'inscription, l'activation et la connexion
 */
export const useAdminAuth = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Mutation pour l'inscription admin
  const registerMutation = useMutation({
    mutationFn: (data) => authApi.registerAdmin(data),
    onSuccess: (data) => {
      setSuccess('Inscription réussie ! Vérifiez votre email pour activer votre compte.');
      setError(null);
      return data;
    },
    onError: (err) => {
      const message = err.response?.data?.detail || 
                     err.response?.data?.email?.[0] ||
                     err.message || 
                     'Erreur lors de l\'inscription';
      setError(message);
      setSuccess(null);
    },
  });

  // Mutation pour l'activation du compte
  const activateMutation = useMutation({
    mutationFn: (token) => authApi.activateAccount(token),
    onSuccess: (data) => {
      // Stocker les données utilisateur retournées par l'API
      if (data.user) {
        storage.setUser(data.user);
      }
      setSuccess('Compte activé avec succès ! Vous pouvez maintenant vous connecter.');
      setError(null);
    },
    onError: (err) => {
      let message = 'Erreur lors de l\'activation du compte';
      
      // Extraire le message d'erreur selon le format
      if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err.response?.data?.token && Array.isArray(err.response.data.token)) {
        message = err.response.data.token[0];
      } else if (err.response?.data?.token && typeof err.response.data.token === 'string') {
        message = err.response.data.token;
      }
      
      setError(message);
      setSuccess(null);
    },
  });

  // Mutation pour la connexion
  const loginMutation = useMutation({
    mutationFn: (credentials) => authApi.login(credentials),
    onSuccess: (data) => {
      setSuccess('Connexion réussie !');
      setError(null);
      return data;
    },
    onError: (err) => {
      const message = err.response?.data?.detail || 
                     err.response?.data?.non_field_errors?.[0] ||
                     'Identifiants incorrects';
      setError(message);
      setSuccess(null);
    },
  });

  // Fonction pour s'inscrire
  const register = useCallback(async (formData) => {
    setError(null);
    setSuccess(null);
    return registerMutation.mutate(formData);
  }, [registerMutation]);

  // Fonction pour activer le compte
  const activate = useCallback((token) => {
    setError(null);
    setSuccess(null);
    activateMutation.mutate(token);
  }, [activateMutation]);

  // Fonction pour se connecter
  const login = useCallback(async (credentials) => {
    setError(null);
    setSuccess(null);
    return loginMutation.mutate(credentials);
  }, [loginMutation]);

  // Fonction pour se déconnecter
  const logout = useCallback(() => {
    storage.clearAll();
    setError(null);
    setSuccess(null);
  }, []);

  return {
    // Mutations
    registerMutation,
    activateMutation,
    loginMutation,
    
    // Fonctions
    register,
    activate,
    login,
    logout,
    
    // État
    error,
    success,
    isLoading: registerMutation.isPending || activateMutation.isPending || loginMutation.isPending,
  };
};
